import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../components/contexts/AuthContext";
import axios from "axios";
import dayjs from "dayjs";
import {
  Badge, Spin, Tag, Drawer, Divider, Timeline, Empty, Collapse,
  Input, Select, DatePicker, Button, message, Tooltip, Popover, Modal, TimePicker, Checkbox,
} from "antd";
import {
  ShoppingCartOutlined, CarOutlined, CheckCircleOutlined,
  UserOutlined, CalendarOutlined, EnvironmentOutlined,
  SearchOutlined, ReloadOutlined, SaveOutlined, PhoneOutlined, CarFilled,
  GlobalOutlined, EditOutlined, TruckOutlined, LinkOutlined, MessageOutlined, WhatsAppOutlined,
} from "@ant-design/icons";
import { API_DOMAIN } from "../components/constants";
import { getCardCountry, mapChatItem } from "../components/qv4/helpers";
import { CustomerEditDrawer } from "../components/qv4/CustomerEditDrawer";
import { WorkflowTaskDrawer } from "../components/qv4/WorkflowTaskDrawer";
import { ChatSidebarColumn } from "../components/qv4/ChatSidebarColumn";
import { ActiveChatDrawer } from "../components/qv4/ActiveChatDrawer";
import { CHAT_API } from "../components/qv4/constants";
import { useSocket } from "../components/contexts/SocketContext";
import { useWatiChat } from "../components/contexts/WatiChatContext";
import { useWatiSocket } from "../components/contexts/WatiSocketContext";

// ─── Status configs ───────────────────────────────────────────────────────────

const PAYMENT_CFG = {
  unpaid:    { label: "Unpaid",    color: "#ff4d4f", bg: "#fff2f0", border: "#ffccc7" },
  partial:   { label: "Partial",   color: "#fa8c16", bg: "#fff7e6", border: "#ffd591" },
  paid:      { label: "Paid",      color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f" },
  postponed: { label: "Postponed", color: "#722ed1", bg: "#f9f0ff", border: "#d3adf7" },
};

const PURCHASE_CFG = {
  waiting_purchase: { label: "Waiting",          color: "#8c8c8c", bg: "#fafafa", border: "#d9d9d9" },
  transfer_pending: { label: "Transfer Pending",  color: "#fa8c16", bg: "#fff7e6", border: "#ffd591" },
  transfer_done:    { label: "Transfer Done",     color: "#1677ff", bg: "#e6f4ff", border: "#91caff" },
  purchased:        { label: "Purchased",         color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f" },
  purchase_issue:   { label: "Issue",             color: "#ff4d4f", bg: "#fff2f0", border: "#ffccc7" },
  cancelled:        { label: "Cancelled",         color: "#595959", bg: "#f5f5f5", border: "#d9d9d9" },
};

const DELIVERY_CFG = {
  not_started:        { label: "Not Started",      color: "#8c8c8c", bg: "#fafafa", border: "#d9d9d9" },
  ready_for_delivery: { label: "Ready",            color: "#1677ff", bg: "#e6f4ff", border: "#91caff" },
  out_for_delivery:   { label: "Out for Delivery", color: "#fa8c16", bg: "#fff7e6", border: "#ffd591" },
  delivered:          { label: "Delivered",        color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f" },
  failed:             { label: "Failed",           color: "#ff4d4f", bg: "#fff2f0", border: "#ffccc7" },
  rescheduled:        { label: "Rescheduled",      color: "#722ed1", bg: "#f9f0ff", border: "#d3adf7" },
};

const LOCATION_CFG = {
  with_supplier: { label: "With Supplier", color: "#8c8c8c" },
  picked_up:     { label: "Picked Up",     color: "#1677ff" },
  at_office:     { label: "At Office",     color: "#52c41a" },
  loaded_to_van: { label: "Loaded to Van", color: "#fa8c16" },
};

// ─── Activity log label / colour maps ────────────────────────────────────────

const ACTIVITY_LABELS = {
  // purchase lifecycle
  waiting_purchase: "Waiting for Purchase",
  transfer_pending: "Transfer Pending",
  transfer_done:    "Transfer Done",
  purchased:        "Purchased",
  purchase_issue:   "Purchase Issue",
  cancelled:        "Cancelled",
  // delivery lifecycle
  ready_for_delivery: "Ready for Delivery",
  out_for_delivery:   "Out for Delivery",
  delivered:          "Delivered",
  failed:             "Delivery Failed",
  rescheduled:        "Delivery Rescheduled",
  // location
  with_supplier: "Item: With Supplier",
  picked_up:     "Item: Picked Up",
  at_office:     "Item: At Office",
  loaded_to_van: "Item: Loaded to Van",
  // payment
  unpaid:    "Payment: Unpaid",
  partial:   "Payment: Partial",
  paid:      "Payment: Paid",
  postponed: "Payment: Postponed",
  // generic
  created:  "Record Created",
  updated:  "Record Updated",
  remarked: "Remark Added",
};

const ACTIVITY_COLORS = {
  purchased:          "#52c41a",
  delivered:          "#52c41a",
  paid:               "#52c41a",
  at_office:          "#52c41a",
  transfer_done:      "#1677ff",
  ready_for_delivery: "#1677ff",
  picked_up:          "#1677ff",
  out_for_delivery:   "#fa8c16",
  loaded_to_van:      "#fa8c16",
  transfer_pending:   "#fa8c16",
  partial:            "#fa8c16",
  purchase_issue:     "#ff4d4f",
  failed:             "#ff4d4f",
  cancelled:          "#ff4d4f",
  postponed:          "#722ed1",
  rescheduled:        "#722ed1",
};

// ─── Column routing ───────────────────────────────────────────────────────────

function getColumn(r) {
  if (r.purchase_status === "cancelled") return null;
  if (r.delivery_status === "delivered") return "completed";
  if (r.purchase_status === "purchased") return "delivery";
  return "purchase";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (val) =>
  val != null
    ? `AED ${Number(val).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

const fmtDate     = (val) => val ? dayjs(val).format("DD MMM YYYY") : "—";
const fmtDateTime = (val) => val ? dayjs(val).format("DD MMM YYYY, h:mm A") : null;

const authHeaders = () => ({ accessToken: localStorage.getItem("accessToken") });

function StatusTag({ cfg, value }) {
  const c = cfg[value];
  if (!c) return <Tag style={{ fontSize: 11, margin: 0 }}>{value ?? "—"}</Tag>;
  return (
    <Tag style={{ fontSize: 11, margin: 0, color: c.color, background: c.bg, border: `1px solid ${c.border}`, fontWeight: 600 }}>
      {c.label}
    </Tag>
  );
}

// ─── Delivery Job Modal ───────────────────────────────────────────────────────

function CreateDeliveryModal({ record, open, onClose, onCreated, onEditCustomer }) {
  const { authState }                         = useAuth();
  const [drivers, setDrivers]                 = useState([]);
  const [driversLoading, setDriversLoading]   = useState(false);
  const [deliveryPersonId, setDeliveryPerson] = useState(null);
  const [selectedAddress, setAddress]         = useState(null);
  const [date, setDate]                       = useState(null);
  const [time, setTime]                       = useState(null);
  const [notes, setNotes]                     = useState("");
  const [sendTrackingLink, setSendTracking]   = useState(true);
  const [trackingUrl, setTrackingUrl]         = useState(null);
  const [trackingMsgResult, setTrackingMsg]   = useState(null);
  const [submitting, setSubmitting]           = useState(false);

  const addresses = record?.customer?.addresses || [];

  useEffect(() => {
    if (!open) return;
    setDeliveryPerson(null); setAddress(null);
    setDate(null); setTime(null); setNotes(""); setSendTracking(true);
    setTrackingUrl(null); setTrackingMsg(null);
    setDriversLoading(true);
    axios.get(`${API_DOMAIN}/shipday/get_all_drivers_info`, {
      headers: { accessToken: localStorage.getItem("accessToken") },
    }).then(res => {
      setDrivers((res.data?.data || []).map(d => ({ value: d.id, label: d.name })));
    }).catch(() => {}).finally(() => setDriversLoading(false));
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedAddress)   { message.warning("Select a delivery address first"); return; }
    if (!deliveryPersonId)  { message.warning("Select a delivery driver"); return; }
    if (!date || !time)     { message.warning("Set the expected delivery date and time"); return; }
    setSubmitting(true);
    try {
      const scheduledAt = dayjs(date).format("YYYY-MM-DD") + " " + dayjs(time).format("HH:mm:ss");
      const payload = {
        customer_name:      record.customer?.name  || "",
        customer_phone:     record.customer?.phone || "",
        delivery_address:   selectedAddress.address,
        ...(selectedAddress.latitude  && { delivery_latitude:  selectedAddress.latitude  }),
        ...(selectedAddress.longitude && { delivery_longitude: selectedAddress.longitude }),
        delivery_notes:      notes || null,
        scheduled_at:        scheduledAt,
        delivery_person_id:  deliveryPersonId ?? null,
        created_by:          authState?.user?.id ?? null,
        send_tracking_link:  sendTrackingLink,
      };
      const res = await axios.post(
        `${API_DOMAIN}/queries/${record.query_pk}/create-delivery-task`,
        payload,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data?.success) throw new Error(res.data?.message || "Failed");
      const { tracking_url, after_sales, delivery_task, tracking_message_result } = res.data.data ?? {};

      // Auto-advance status to out_for_delivery
      const afterSalesId = after_sales?.id;
      if (afterSalesId) {
        axios.post(
          `${API_DOMAIN}/query_after_sales/update`,
          { id: afterSalesId, delivery_status: "out_for_delivery" },
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        ).catch(() => {});
      }

      const merged = after_sales
        ? { ...after_sales, delivery_status: "out_for_delivery", current_delivery_task: delivery_task ?? null }
        : null;

      setTrackingMsg(tracking_message_result ?? null);
      if (tracking_url) {
        setTrackingUrl(tracking_url);
        onCreated?.({ after_sales: merged, delivery_task });
      } else {
        message.success("Delivery job created");
        onCreated?.({ after_sales: merged, delivery_task });
        onClose();
      }
    } catch (e) {
      message.error(e.message || "Failed to create delivery job");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div>
          <div style={{ fontWeight: 700 }}>Create Delivery Job</div>
          {record?.customer?.name && (
            <div style={{ fontSize: 12, color: "#8c8c8c", fontWeight: 400 }}>
              {record.customer.name} · Query #{record.query_id}
            </div>
          )}
        </div>
      }
      width={620}
      maskClosable={false}
      footer={
        trackingUrl ? (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => { onCreated?.({}); onClose(); }}>Close</Button>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" icon={<TruckOutlined />} loading={submitting} onClick={handleSubmit}>
              Create Delivery Job
            </Button>
          </div>
        )
      }
    >
      {trackingUrl ? (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: "#52c41a", marginBottom: 12 }} />
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Delivery job created!</div>
          <div style={{ marginBottom: 16, color: "#8c8c8c" }}>Tracking link:</div>
          <a href={trackingUrl} target="_blank" rel="noreferrer"
            style={{ wordBreak: "break-all", color: "#1677ff" }}>{trackingUrl}</a>
          {trackingMsgResult && (
            <div style={{
              marginTop: 16, padding: "8px 12px", borderRadius: 6, fontSize: 12,
              background: trackingMsgResult.success ? "#f6ffed" : "#fff2f0",
              border: `1px solid ${trackingMsgResult.success ? "#b7eb8f" : "#ffccc7"}`,
              color: trackingMsgResult.success ? "#389e0d" : "#cf1322",
            }}>
              {trackingMsgResult.success
                ? "Tracking link sent to customer via WhatsApp"
                : "Delivery created · tracking message failed to send"}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "4px 0" }}>

          {/* Address picker */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Delivery Address</span>
              <Button size="small" onClick={onEditCustomer}>+ Add / Edit Addresses</Button>
            </div>
            {addresses.length === 0 ? (
              <div style={{
                border: "1px dashed #d9d9d9", borderRadius: 8, padding: "24px 16px",
                textAlign: "center", color: "#8c8c8c",
              }}>
                <EnvironmentOutlined style={{ fontSize: 24, marginBottom: 8, display: "block" }} />
                No addresses on file for this customer.
                <div style={{ marginTop: 8 }}>
                  <Button size="small" onClick={onEditCustomer}>Add Address</Button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
                {addresses.map((addr, i) => {
                  const isSelected = selectedAddress?.id === addr.id || (!addr.id && selectedAddress === addr);
                  return (
                    <div
                      key={addr.id ?? i}
                      onClick={() => setAddress(addr)}
                      style={{
                        border: `1.5px solid ${isSelected ? "#1677ff" : "#e8e8e8"}`,
                        borderRadius: 8, padding: "10px 14px", cursor: "pointer",
                        background: isSelected ? "#e6f4ff" : "#fff",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{
                          fontSize: 11, background: "#f0f0f0", color: "#595959",
                          padding: "1px 7px", borderRadius: 4, fontWeight: 600,
                        }}>{addr.type || "Address"}</span>
                        {isSelected && <CheckCircleOutlined style={{ color: "#1677ff" }} />}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 13, marginTop: 4 }}>
                        {addr.name}
                        {addr.number && <span style={{ fontWeight: 400, color: "#8c8c8c", fontSize: 12, marginLeft: 10 }}>{addr.number}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "#595959", marginTop: 2 }}>{addr.address}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Delivery driver */}
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Delivery Driver</div>
            <Select
              showSearch
              placeholder="Select delivery driver…"
              style={{ width: "100%" }}
              value={deliveryPersonId}
              onChange={v => setDeliveryPerson(v ?? null)}
              options={drivers}
              optionFilterProp="label"
              loading={driversLoading}
              notFoundContent={driversLoading ? "Loading drivers…" : "No drivers found"}
            />
          </div>

          {/* Date + Time */}
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Scheduled Date</div>
              <DatePicker
                style={{ width: "100%" }}
                placeholder="Delivery date"
                value={date}
                onChange={setDate}
                disabledDate={d => d && d < dayjs().startOf("day")}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Scheduled Time</div>
              <TimePicker
                style={{ width: "100%" }}
                use12Hours
                format="h:mm a"
                placeholder="Delivery time"
                value={time}
                onChange={setTime}
                disabledTime={() => {
                  if (date && dayjs(date).isAfter(dayjs(), "day")) return {};
                  const now = dayjs();
                  return {
                    disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
                    disabledMinutes: h => h === now.hour()
                      ? Array.from({ length: now.minute() }, (_, i) => i) : [],
                  };
                }}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Delivery Notes</div>
            <Input.TextArea
              rows={2}
              placeholder="Any special delivery instructions…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>

          {/* Tracking link */}
          <Checkbox
            checked={sendTrackingLink}
            onChange={e => setSendTracking(e.target.checked)}
          >
            <span style={{ fontSize: 13 }}>Send tracking link to customer via WhatsApp</span>
          </Checkbox>
        </div>
      )}
    </Modal>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function AfterSalesCard({ record, userMap, userList = [], shipdayDrivers = [], shipdayDriverMap = {}, onQuickPatch, onClick, onOpenChat }) {
  const [statusPopover, setStatusPopover]               = useState(false);
  const [deliveryPopover, setDeliveryPopover]           = useState(false);
  const [purchaserPopover, setPurchaserPopover]         = useState(false);
  const [deliveryPersonPopover, setDeliveryPersonPop]   = useState(false);

  const pc          = PURCHASE_CFG[record.purchase_status] || {};
  const loc         = LOCATION_CFG[record.item_location_status];
  const cust        = record.customer;
  const veh         = record.vehicle;
  const parts       = record.parts || [];
  const cardCountry = getCardCountry(cust?.phone);
  const isIntl      = cardCountry && cardCountry.name !== "United Arab Emirates";

  const quickPatch = (field, value, closePopover) => {
    onQuickPatch?.(record.id, field, value);
    closePopover(false);
  };

  return (
    <div
      onClick={onClick}
      style={{
        position: "relative",
        backgroundColor: "#fff",
        borderRadius: 8,
        border: "1px solid #e8e8e8",
        borderLeft: `4px solid ${pc.color || "#d9d9d9"}`,
        padding: "12px 14px",
        cursor: "pointer",
        transition: "box-shadow 0.15s",
        display: "flex", flexDirection: "column", gap: 6,
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      {/* Globe watermark for international orders */}
      {isIntl && (
        <GlobalOutlined style={{
          position: "absolute", bottom: 10, right: 12,
          fontSize: 64, color: "rgba(22,119,255,0.06)",
          pointerEvents: "none",
        }} />
      )}
      {/* Query ID + payment status */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: "#1677ff" }}>#{record.query_id}</span>
        <StatusTag cfg={PAYMENT_CFG} value={record.payment_status} />
      </div>

      {/* Customer + Vehicle/Parts row */}
      {(cust || veh || parts.length > 0) && (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          {/* Left: customer */}
          {cust && (
            <div style={{ flex: 1, minWidth: 0, border: "1px solid #f0f0f0", borderRadius: 6, padding: "6px 8px" }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: "#262626", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cust.name}</div>
              {cust.phone && (
                <div style={{ fontSize: 11, color: "#8c8c8c", display: "flex", alignItems: "center", gap: 3, marginTop: 2 }}>
                  {cardCountry ? (
                    <Tooltip title={cardCountry.name}>
                      <img
                        src={`https://cdn.clare.ai/flags/${cardCountry.code}.svg`}
                        alt={cardCountry.name}
                        style={{ width: 12, height: 12, objectFit: "contain", borderRadius: 2, flexShrink: 0, cursor: "default" }}
                      />
                    </Tooltip>
                  ) : (
                    <PhoneOutlined style={{ fontSize: 9 }} />
                  )}
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cust.phone}</span>
                  {onOpenChat && (
                    <Tooltip title="Open WhatsApp chat">
                      <WhatsAppOutlined
                        onClick={e => { e.stopPropagation(); onOpenChat(cust.phone); }}
                        style={{ fontSize: 12, color: "#25d366", cursor: "pointer", flexShrink: 0 }}
                      />
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Right: vehicle + parts */}
          {(veh || parts.length > 0) && (
            <div style={{ flex: 1, minWidth: 0, border: "1px solid #f0f0f0", borderRadius: 6, padding: "6px 8px", display: "flex", flexDirection: "column", gap: 4 }}>
              {veh && (
                <div style={{ fontSize: 11, color: "#595959", display: "flex", alignItems: "center", gap: 4 }}>
                  <CarFilled style={{ fontSize: 10, color: "#8c8c8c" }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {[veh.brand, veh.model, veh.year].filter(Boolean).join(" ")}
                  </span>
                </div>
              )}
              {parts.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  {parts.slice(0, 2).map((p, i) => (
                    <span key={i} style={{
                      fontSize: 10, background: "#f0f5ff", color: "#2f54eb",
                      border: "1px solid #d6e4ff", borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap",
                    }}>
                      {p.part_name}{p.quantity > 1 ? ` ×${p.quantity}` : ""}{p.side ? ` (${p.side})` : ""}
                    </span>
                  ))}
                  {parts.length > 2 && (
                    <span style={{
                      fontSize: 10, background: "#f5f5f5", color: "#8c8c8c",
                      border: "1px solid #d9d9d9", borderRadius: 4, padding: "1px 5px",
                    }}>+{parts.length - 2}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Financial summary — single row */}
      <div style={{ display: "flex", gap: 6, fontSize: 11, alignItems: "center", background: "#fafafa", borderRadius: 6, padding: "5px 8px" }}>
        <span style={{ color: "#8c8c8c" }}>Sale</span>
        <span style={{ fontWeight: 600, color: "#262626" }}>{fmt(record.sale_amount)}</span>
        <span style={{ color: "#d9d9d9" }}>·</span>
        <span style={{ color: "#8c8c8c" }}>Paid</span>
        <span style={{ fontWeight: 600, color: "#52c41a" }}>{fmt(record.paid_amount)}</span>
        <span style={{ color: "#d9d9d9" }}>·</span>
        <span style={{ color: "#8c8c8c" }}>Bal</span>
        <span style={{ fontWeight: 600, color: Number(record.balance_amount) > 0 ? "#ff4d4f" : "#52c41a" }}>
          {fmt(record.balance_amount)}
        </span>
      </div>

      {/* Pipeline flow row: Purchaser › Purchase Status › Driver › Delivery Status */}
      {(() => {
        const isPurchased = record.purchase_status === "purchased";
        const hasTask     = !!record.current_delivery_task;
        const arrow = (active) => (
          <span style={{ color: active ? "#595959" : "#d9d9d9", fontSize: 14, flexShrink: 0, lineHeight: 1 }}>›</span>
        );
        const chip = ({ icon: Icon, label, active, dashed }) => (
          <div style={{
            display: "flex", alignItems: "center", gap: 3,
            fontSize: 11, padding: "1px 7px", borderRadius: 20, flexShrink: 0,
            border: active
              ? (dashed ? "1px dashed #bfbfbf" : "1px solid #d9d9d9")
              : "1px solid #f0f0f0",
            color: active ? (dashed ? "#bfbfbf" : "#262626") : "#d9d9d9",
            background: active && !dashed ? "#fafafa" : "transparent",
            cursor: active ? "pointer" : "default",
            transition: "all 0.15s",
          }}>
            <Icon style={{ fontSize: 10 }} />
            {label}
          </div>
        );

        return (
          <div onClick={e => e.stopPropagation()}
            style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>

            {/* 1. Purchaser */}
            <Popover
              open={purchaserPopover} onOpenChange={setPurchaserPopover}
              trigger="click" placement="bottomLeft"
              content={
                <div style={{ width: 200 }} onClick={e => e.stopPropagation()}>
                  <Select autoFocus showSearch allowClear style={{ width: "100%" }}
                    placeholder="Assign purchaser…"
                    value={record.purchaser_id ? Number(record.purchaser_id) : undefined}
                    optionFilterProp="label"
                    options={groupedUserOptions(userList, "Purchaser")}
                    onChange={v => quickPatch("purchaser_id", v ?? null, setPurchaserPopover)}
                  />
                </div>
              }
            >
              {chip({
                icon: UserOutlined,
                label: record.purchaser_id ? (userMap[record.purchaser_id] || `#${record.purchaser_id}`) : "Purchaser",
                active: true,
                dashed: !record.purchaser_id,
              })}
            </Popover>

            {arrow(true)}

            {/* 2. Purchase Status */}
            <Popover
              open={statusPopover} onOpenChange={setStatusPopover}
              trigger="click" placement="bottomLeft"
              content={
                <div style={{ width: 190 }} onClick={e => e.stopPropagation()}>
                  {Object.entries(PURCHASE_CFG).map(([k, v]) => (
                    <div key={k}
                      onClick={() => quickPatch("purchase_status", k, setStatusPopover)}
                      style={{
                        padding: "6px 10px", borderRadius: 4, cursor: "pointer",
                        fontWeight: record.purchase_status === k ? 600 : 400,
                        background: record.purchase_status === k ? v.bg : "transparent",
                        color: v.color, transition: "background 0.15s",
                      }}
                      onMouseEnter={e => { if (record.purchase_status !== k) e.currentTarget.style.background = "#f5f5f5"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = record.purchase_status === k ? v.bg : "transparent"; }}
                    >{v.label}</div>
                  ))}
                </div>
              }
            >
              <div style={{ cursor: "pointer" }}>
                <StatusTag cfg={PURCHASE_CFG} value={record.purchase_status} />
              </div>
            </Popover>

            {arrow(isPurchased)}

            {/* 3. Delivery Driver */}
            {isPurchased ? (
              <Popover
                open={deliveryPersonPopover} onOpenChange={setDeliveryPersonPop}
                trigger="click" placement="bottomLeft"
                content={
                  <div style={{ width: 200 }} onClick={e => e.stopPropagation()}>
                    <Select autoFocus showSearch allowClear style={{ width: "100%" }}
                      placeholder="Assign driver…"
                      value={record.delivery_person_id ? Number(record.delivery_person_id) : undefined}
                      optionFilterProp="label"
                      options={shipdayDrivers}
                      onChange={v => quickPatch("delivery_person_id", v ?? null, setDeliveryPersonPop)}
                    />
                  </div>
                }
              >
                {chip({
                  icon: CarOutlined,
                  label: (record.delivery_person_id && shipdayDriverMap[record.delivery_person_id]) || "Driver",
                  active: true,
                  dashed: !record.delivery_person_id,
                })}
              </Popover>
            ) : (
              chip({ icon: CarOutlined, label: "Driver", active: false })
            )}

            {arrow(hasTask)}

            {/* 4. Delivery Status */}
            {hasTask ? (
              <Popover
                open={deliveryPopover} onOpenChange={setDeliveryPopover}
                trigger="click" placement="bottomLeft"
                content={
                  <div style={{ width: 190 }} onClick={e => e.stopPropagation()}>
                    {Object.entries(DELIVERY_CFG).map(([k, v]) => (
                      <div key={k}
                        onClick={() => quickPatch("delivery_status", k, setDeliveryPopover)}
                        style={{
                          padding: "6px 10px", borderRadius: 4, cursor: "pointer",
                          fontWeight: record.delivery_status === k ? 600 : 400,
                          background: record.delivery_status === k ? v.bg : "transparent",
                          color: v.color, transition: "background 0.15s",
                        }}
                        onMouseEnter={e => { if (record.delivery_status !== k) e.currentTarget.style.background = "#f5f5f5"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = record.delivery_status === k ? v.bg : "transparent"; }}
                      >{v.label}</div>
                    ))}
                  </div>
                }
              >
                <div style={{ cursor: "pointer" }}>
                  <StatusTag cfg={DELIVERY_CFG} value={record.delivery_status} />
                </div>
              </Popover>
            ) : (
              <div style={{
                fontSize: 11, padding: "1px 7px", borderRadius: 20,
                border: "1px solid #f0f0f0", color: "#d9d9d9",
              }}>Delivery</div>
            )}
          </div>
        );
      })()}

      {/* Location / delivery person / date */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12, color: "#595959" }}>
        {loc && (
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <EnvironmentOutlined style={{ color: loc.color, fontSize: 11 }} />
            <span style={{ color: loc.color, fontWeight: 600 }}>{loc.label}</span>
          </span>
        )}
        {record.delivery_person_id && userMap[record.delivery_person_id] && (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <CarOutlined style={{ fontSize: 11 }} />
            {userMap[record.delivery_person_id]}
          </span>
        )}
        {record.expected_purchase_date && (
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <CalendarOutlined style={{ fontSize: 11, color: "#8c8c8c" }} />
            Expected: {fmtDate(record.expected_purchase_date)}
          </span>
        )}
      </div>

      {/* Delivery job — only on purchased cards */}
      {record.purchase_status === "purchased" && (
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 2 }}>
          {record.current_delivery_task ? (
            (() => {
              const dt = record.current_delivery_task;
              const isDelivered = record.delivery_status === "delivered";
              const dcfg = DELIVERY_CFG[record.delivery_status] || DELIVERY_CFG.out_for_delivery;
              return (
                <div style={{
                  border: `1px solid ${isDelivered ? "#b7eb8f" : dcfg.border}`,
                  borderRadius: 6,
                  background: isDelivered ? "#f6ffed" : dcfg.bg,
                  padding: "5px 8px", fontSize: 11,
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
                }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, color: dcfg.color, fontWeight: 600 }}>
                    <TruckOutlined />
                    {dcfg.label}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, color: "#595959" }}>
                    {isDelivered && dt.scheduled_at && record.delivered_at ? (
                      <>
                        <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          <CalendarOutlined style={{ fontSize: 10 }} />
                          {dayjs(dt.scheduled_at).format("DD MMM, h:mm a")}
                        </span>
                        <span style={{ color: "#595959", fontSize: 14, lineHeight: 1 }}>›</span>
                        <span style={{ display: "flex", alignItems: "center", gap: 3, color: "#389e0d", fontWeight: 600 }}>
                          <CheckCircleOutlined style={{ fontSize: 10 }} />
                          {dayjs(record.delivered_at).format("DD MMM, h:mm a")}
                        </span>
                      </>
                    ) : (
                      <>
                        {dt.scheduled_at && (
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <CalendarOutlined style={{ fontSize: 10 }} />
                            {dayjs(dt.scheduled_at).format("DD MMM, h:mm a")}
                          </span>
                        )}
                        {dt.tracking_url && (
                          <a href={dt.tracking_url} target="_blank" rel="noreferrer"
                            style={{ color: "#1677ff" }} onClick={e => e.stopPropagation()}>
                            <LinkOutlined />
                          </a>
                        )}
                      </>
                    )}
                  </span>
                </div>
              );
            })()
          ) : (
            <Button
              size="small"
              icon={<TruckOutlined />}
              onClick={() => onQuickPatch?.(record.id, "__openDelivery", true)}
              style={{ width: "100%", fontSize: 11, borderStyle: "dashed" }}
            >
              Create Delivery Job
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Drawer ───────────────────────────────────────────────────────────────────

const PROCESS_STEPS = [
  { key: "expected_purchase_date", label: "Expected Purchase" },
  { key: "purchased_at",           label: "Purchased" },
  { key: "received_at_office",     label: "Received at Office" },
  { key: "loaded_to_van_at",       label: "Loaded to Van" },
  { key: "out_for_delivery_at",    label: "Out for Delivery" },
  { key: "delivered_at",           label: "Delivered" },
];

function Section({ title, children, action }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 11, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: 1 }}>
          {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f5f5f5", fontSize: 13, gap: 8 }}>
      <span style={{ color: "#8c8c8c", flexShrink: 0 }}>{label}</span>
      <div style={{ fontWeight: 500, color: "#262626", textAlign: "right", flex: 1 }}>
        {children}
      </div>
    </div>
  );
}

const userOptions = (list) => list.map(u => ({ value: u.id, label: u.fullName }));

function groupedUserOptions(list, priorityType = null) {
  const groups = {};
  list.forEach(u => {
    const key = u.user_type?.trim() || "Other";
    if (!groups[key]) groups[key] = [];
    groups[key].push({ value: u.id, label: u.fullName });
  });
  return Object.entries(groups)
    .sort(([a], [b]) => {
      if (priorityType) {
        if (a === priorityType) return -1;
        if (b === priorityType) return 1;
      }
      return a.localeCompare(b);
    })
    .map(([label, options]) => ({ label, options }));
}

const statusSelectStyle = { width: "100%", maxWidth: 200 };

function AfterSalesDrawer({ record, onClose, userMap, userList, onUpdate, onCreateDelivery }) {
  const [patchingField, setPatchingField] = useState(null);
  const [draft, setDraft]           = useState({});
  const [saving, setSaving]         = useState(false);
  const [custDrawerOpen, setCustDrawerOpen]   = useState(false);
  const [taskDrawerOpen, setTaskDrawerOpen]   = useState(false);

  useEffect(() => { setDraft({}); setCustDrawerOpen(false); setTaskDrawerOpen(false); }, [record?.id]);

  if (!record) return null;

  const cur = { ...record, ...draft };

  const hasDraft = Object.keys(draft).some(k => draft[k] !== record[k]);

  // Auto-save a single status field immediately
  const patchStatus = async (field, value) => {
    const prev = record[field];
    onUpdate({ id: record.id, [field]: value });
    setPatchingField(field);
    try {
      await axios.post(
        `${API_DOMAIN}/query_after_sales/update`,
        { id: record.id, [field]: value },
        { headers: authHeaders() }
      );
      message.success("Status updated");
    } catch {
      onUpdate({ id: record.id, [field]: prev });
      message.error("Failed to update status");
    } finally {
      setPatchingField(null);
    }
  };

  const setField = (field, value) => setDraft(d => ({ ...d, [field]: value }));

  const saveDraft = async () => {
    const payload = {};
    Object.keys(draft).forEach(k => { if (draft[k] !== record[k]) payload[k] = draft[k]; });
    if (!Object.keys(payload).length) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_DOMAIN}/query_after_sales/update`,
        { id: record.id, ...payload },
        { headers: authHeaders() }
      );
      const updated = res.data?.data?.after_sales ?? {};
      onUpdate({ id: record.id, ...payload, ...updated });
      setDraft({});
      message.success("Saved");
    } catch {
      message.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const timelineItems = PROCESS_STEPS
    .filter(s => record[s.key])
    .map(s => ({
      color: "#1677ff",
      label: <span style={{ fontSize: 11, color: "#8c8c8c", whiteSpace: "nowrap" }}>{fmtDateTime(record[s.key])}</span>,
      children: <span style={{ fontSize: 13, fontWeight: 500 }}>{s.label}</span>,
    }));

  const activityLog = (() => {
    try {
      const log = typeof record.timeline === "string" ? JSON.parse(record.timeline) : record.timeline;
      return Array.isArray(log) ? log : [];
    } catch { return []; }
  })();

  return (
    <>
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 700 }}>Operations · Query #{record.query_id}</span>
          <StatusTag cfg={PAYMENT_CFG} value={record.payment_status} />
        </div>
      }
      open
      onClose={onClose}
      width={500}
      styles={{ body: { padding: "20px 24px" } }}
      extra={
        <div style={{ display: "flex", gap: 8 }}>
          {record.query_pk && (
            <Button size="small" onClick={() => setTaskDrawerOpen(true)}>
              Tasks
            </Button>
          )}
          {hasDraft && (
            <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} onClick={saveDraft}>
              Save Changes
            </Button>
          )}
        </div>
      }
    >
      {/* ── Customer ── */}
      {record.customer && (
        <>
          <Section
            title="Customer"
            action={
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => setCustDrawerOpen(true)}
              >
                Edit
              </Button>
            }
          >
            <Row label="Name">{record.customer.name}</Row>
            <Row label="Phone">{record.customer.phone || "—"}</Row>
            {record.customer.email && <Row label="Email">{record.customer.email}</Row>}
            {record.customer.country && <Row label="Country">{record.customer.country}</Row>}
          </Section>
          <Divider style={{ margin: "0 0 20px" }} />
        </>
      )}

      {/* ── Vehicle ── */}
      {record.vehicle && (
        <>
          <Section title="Vehicle">
            <Row label="Make / Model">
              {[record.vehicle.brand, record.vehicle.model, record.vehicle.year].filter(Boolean).join(" ")}
            </Row>
            {record.vehicle.vin && <Row label="VIN">{record.vehicle.vin}</Row>}
            {record.vehicle.country_spec && <Row label="Spec">{record.vehicle.country_spec}</Row>}
          </Section>
          <Divider style={{ margin: "0 0 20px" }} />
        </>
      )}

      {/* ── Parts ── */}
      {record.parts?.length > 0 && (
        <>
          <Section title={`Parts (${record.parts.length})`}>
            {record.parts.map((p, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                padding: "7px 0", borderBottom: "1px solid #f5f5f5", fontSize: 13,
              }}>
                <div>
                  <span style={{ fontWeight: 500 }}>{p.part_name}</span>
                  {p.side && <span style={{ color: "#8c8c8c", fontSize: 11, marginLeft: 6 }}>{p.side}</span>}
                  {p.quantity > 1 && <span style={{ color: "#8c8c8c", fontSize: 11, marginLeft: 6 }}>×{p.quantity}</span>}
                  {p.part_number && <div style={{ fontSize: 11, color: "#bfbfbf" }}>{p.part_number}</div>}
                </div>
                <div style={{ textAlign: "right" }}>
                  {p.selling_price != null && (
                    <div style={{ fontWeight: 600, color: "#262626" }}>{fmt(p.selling_price)}</div>
                  )}
                  {p.warranty_days != null && (
                    <div style={{ fontSize: 11, color: "#8c8c8c" }}>{p.warranty_days}d warranty</div>
                  )}
                </div>
              </div>
            ))}
          </Section>
          <Divider style={{ margin: "0 0 20px" }} />
        </>
      )}

      {/* ── Reference ── */}
      <Section title="Reference">
        <Row label="Query ID">{`#${record.query_id}`}</Row>
        <Row label="SO Number">
          <Input
            size="small"
            value={cur.so_number ?? ""}
            onChange={e => setField("so_number", e.target.value)}
            placeholder="—"
            style={{ maxWidth: 180, textAlign: "right" }}
            variant="borderless"
          />
        </Row>
        <Row label="PO Number">
          <Input
            size="small"
            value={cur.po_number ?? ""}
            onChange={e => setField("po_number", e.target.value)}
            placeholder="—"
            style={{ maxWidth: 180, textAlign: "right" }}
            variant="borderless"
          />
        </Row>
        <Row label="Created">{fmtDateTime(record.created_at) ?? "—"}</Row>
      </Section>

      <Divider style={{ margin: "0 0 20px" }} />

      {/* ── Financials ── */}
      <Section title="Financials">
        <Row label="Sale Amount">{fmt(record.sale_amount)}</Row>
        <Row label="Paid Amount">{fmt(record.paid_amount)}</Row>
        <Row label="Balance">{fmt(record.balance_amount)}</Row>
        <Row label="Payment Status"><StatusTag cfg={PAYMENT_CFG} value={record.payment_status} /></Row>
        <Row label="Purchase Cost">{fmt(record.purchase_cost)}</Row>
        <Row label="Transfer Amount">{fmt(record.purchaser_transfer_amount)}</Row>
        <Row label="Transfer Status">{record.purchaser_transfer_status ?? "—"}</Row>
        {record.payment_reminder_at && (
          <Row label="Payment Reminder">{fmtDateTime(record.payment_reminder_at)}</Row>
        )}
      </Section>

      <Divider style={{ margin: "0 0 20px" }} />

      {/* ── Purchase stage ── */}
      <Section title="Purchase">
        <Row label="Status">
          <Select
            size="small"
            value={cur.purchase_status}
            onChange={v => patchStatus("purchase_status", v)}
            loading={patchingField === "purchase_status"}
            style={statusSelectStyle}
            options={Object.entries(PURCHASE_CFG).map(([k, v]) => ({ value: k, label: v.label }))}
          />
        </Row>
        <Row label="Item Location">
          <Select
            size="small"
            value={cur.item_location_status}
            onChange={v => patchStatus("item_location_status", v)}
            loading={patchingField === "item_location_status"}
            style={statusSelectStyle}
            options={Object.entries(LOCATION_CFG).map(([k, v]) => ({ value: k, label: v.label }))}
            allowClear
          />
        </Row>
        <Row label="Purchaser">
          <Select
            size="small"
            value={cur.purchaser_id ? Number(cur.purchaser_id) : undefined}
            onChange={v => setField("purchaser_id", v)}
            style={statusSelectStyle}
            options={groupedUserOptions(userList, "Purchaser")}
            showSearch
            optionFilterProp="label"
            placeholder="Assign purchaser"
            allowClear
          />
        </Row>
        <Row label="Expected Date">
          <DatePicker
            size="small"
            value={cur.expected_purchase_date ? dayjs(cur.expected_purchase_date) : null}
            onChange={d => setField("expected_purchase_date", d ? d.toISOString() : null)}
            format="DD MMM YYYY"
            style={{ maxWidth: 160 }}
            placeholder="Set date"
            variant="borderless"
          />
        </Row>
        <Row label="Created By">{record.created_by ? (userMap[record.created_by] || `#${record.created_by}`) : "—"}</Row>
      </Section>

      <Divider style={{ margin: "0 0 20px" }} />

      {/* ── Delivery stage — only after purchase complete ── */}
      {cur.purchase_status === "purchased" ? (
        <>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#f6ffed", border: "1px solid #b7eb8f",
            borderRadius: 6, padding: "8px 12px", marginBottom: 20,
          }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#389e0d", fontWeight: 500 }}>
              <CheckCircleOutlined style={{ fontSize: 15 }} />
              Purchase complete
            </span>
            {!cur.current_delivery_task && (
              <Button
                size="small"
                type="primary"
                icon={<TruckOutlined />}
                onClick={() => onCreateDelivery?.(record)}
                style={{ background: "#389e0d", borderColor: "#389e0d" }}
              >
                Create Delivery Job
              </Button>
            )}
          </div>

          {/* Active delivery task card */}
          {cur.current_delivery_task && (() => {
            const dt = cur.current_delivery_task;
            return (
              <div style={{
                border: "1px solid #91caff", borderRadius: 8,
                background: "#e6f4ff", padding: "12px 14px", marginBottom: 20,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 13, color: "#0958d9" }}>
                    <TruckOutlined /> Active Delivery Task
                  </span>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <Tag color={DELIVERY_CFG[cur.delivery_status]?.color || "default"} style={{ margin: 0, fontSize: 11 }}>
                      {DELIVERY_CFG[cur.delivery_status]?.label || dt.provider_status || cur.delivery_status}
                    </Tag>
                    {dt.tracking_url && (
                      <a href={dt.tracking_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                        <LinkOutlined /> Track
                      </a>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 4, color: "#262626" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <EnvironmentOutlined style={{ color: "#8c8c8c", marginTop: 2 }} />
                    <span>{dt.delivery_address}</span>
                  </div>
                  {dt.scheduled_at && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <CalendarOutlined style={{ color: "#8c8c8c", marginTop: 2 }} />
                      <span>Scheduled: {dayjs(dt.scheduled_at).format("DD MMM YYYY, h:mm a")}</span>
                    </div>
                  )}
                  {dt.remarks && (
                    <div style={{ color: "#595959", marginTop: 2 }}>{dt.remarks}</div>
                  )}
                </div>
              </div>
            );
          })()}

          <Section title="Delivery">
            <Row label="Status">
              <Select
                size="small"
                value={cur.delivery_status}
                onChange={v => patchStatus("delivery_status", v)}
                loading={patchingField === "delivery_status"}
                style={statusSelectStyle}
                options={Object.entries(DELIVERY_CFG).map(([k, v]) => ({ value: k, label: v.label }))}
                allowClear
              />
            </Row>
            <Row label="Delivery Person">
              <Select
                size="small"
                value={cur.delivery_person_id ? Number(cur.delivery_person_id) : undefined}
                onChange={v => setField("delivery_person_id", v)}
                style={statusSelectStyle}
                options={groupedUserOptions(userList, "Delivery Person")}
                showSearch
                optionFilterProp="label"
                placeholder="Assign delivery"
                allowClear
              />
            </Row>
            {cur.expected_delivery_at && (
              <Row label="Expected Delivery">
                {dayjs(cur.expected_delivery_at).format("DD MMM YYYY, h:mm a")}
              </Row>
            )}
          </Section>
          <Divider style={{ margin: "0 0 20px" }} />
        </>
      ) : (
        <div style={{
          border: "1px dashed #d9d9d9", borderRadius: 6,
          padding: "12px 16px", marginBottom: 20,
          textAlign: "center", fontSize: 12, color: "#bfbfbf",
        }}>
          Delivery section unlocks when purchase status is set to <strong>Purchased</strong>
        </div>
      )}

      {timelineItems.length > 0 && (
        <>
          <Divider style={{ margin: "0 0 20px" }} />
          <Section title="Process Timeline">
            <Timeline mode="left" items={timelineItems} />
          </Section>
        </>
      )}

      {/* ── Remarks ── */}
      <Divider style={{ margin: "0 0 20px" }} />
      <Section title="Remarks">
        <Input.TextArea
          value={cur.remarks ?? ""}
          onChange={e => setField("remarks", e.target.value)}
          placeholder="Add remarks…"
          autoSize={{ minRows: 2, maxRows: 6 }}
          style={{ fontSize: 13, color: "#262626", lineHeight: 1.7, background: "#fafafa", borderRadius: 6, border: "1px solid #f0f0f0" }}
        />
      </Section>

      {activityLog.length > 0 && (
        <>
          <Divider style={{ margin: "0 0 20px" }} />
          <Section title="Activity Log">
            <Timeline
              items={[...activityLog].sort((a, b) => new Date(b.time ?? b.at) - new Date(a.time ?? a.at)).map((entry, i) => {
                const rawKey = entry.event ?? entry.status ?? entry.action ?? "";
                const label  = ACTIVITY_LABELS[rawKey] ?? (rawKey.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "Event");
                const ts     = entry.time ?? entry.at ?? entry.timestamp;
                const who    = entry.user_id ? (userMap[entry.user_id] ?? `#${entry.user_id}`) : null;
                return {
                  key: i,
                  color: ACTIVITY_COLORS[rawKey] ?? "#8c8c8c",
                  children: (
                    <div style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 600 }}>{label}</span>
                      {entry.details && (
                        <div style={{ color: "#595959", marginTop: 2 }}>{entry.details}</div>
                      )}
                      <div style={{ color: "#bfbfbf", fontSize: 11, marginTop: 2, display: "flex", gap: 6 }}>
                        {who && <span style={{ color: "#8c8c8c", fontWeight: 500 }}>{who}</span>}
                        {who && ts && <span>·</span>}
                        {ts && <span>{fmtDateTime(ts)}</span>}
                      </div>
                    </div>
                  ),
                };
              })}
            />
          </Section>
        </>
      )}

      {/* Floating save bar */}
      {hasDraft && (
        <div style={{ position: "sticky", bottom: 0, background: "#fff", padding: "12px 0 0", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button size="small" onClick={() => setDraft({})}>Discard</Button>
          <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} onClick={saveDraft}>
            Save Changes
          </Button>
        </div>
      )}
    </Drawer>

    <CustomerEditDrawer
      open={custDrawerOpen}
      onClose={() => setCustDrawerOpen(false)}
      customer={record.customer ? {
        customer_id:           record.customer.id,
        customer_name:         record.customer.name,
        customer_real_name:    record.customer.real_name || "",
        customer_mobile:       record.customer.phone,
        customer_email:        record.customer.email || "",
        customer_addresses_new: record.customer.addresses || [],
        country:               record.customer.country || "",
        source:                null,
        status:                1,
        type:                  null,
      } : null}
      onSaved={(saved) => {
        onUpdate?.({ ...record, customer: { ...record.customer, name: saved.customer_name || record.customer.name, email: saved.customer_email || record.customer.email } });
        setCustDrawerOpen(false);
      }}
    />

    <WorkflowTaskDrawer
      queryPk={taskDrawerOpen ? record.query_pk : null}
      onClose={() => setTaskDrawerOpen(false)}
    />
    </>
  );
}

// ─── Warranty helper ──────────────────────────────────────────────────────────
// Returns true if the record still has at least one part with active warranty
// counted from delivered_at. Parts without warranty_days are ignored.
function isUnderWarranty(r) {
  if (!r.delivered_at) return false;
  const deliveredDay = dayjs(r.delivered_at);
  return (r.parts || []).some(p =>
    p.warranty_days > 0 && dayjs().diff(deliveredDay, "day") < p.warranty_days
  );
}

// ─── Column definitions ───────────────────────────────────────────────────────

const COLUMNS = [
  { key: "purchase",  label: "Purchase",  icon: <ShoppingCartOutlined />, color: "#fa8c16" },
  { key: "delivery",  label: "Delivery",  icon: <CarOutlined />,           color: "#1677ff" },
  { key: "completed", label: "Completed", icon: <CheckCircleOutlined />,   color: "#52c41a" },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TaskManagement() {
  const [records, setRecords]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [selectedRecord, setSelected]       = useState(null);
  const [deliveryRecord, setDeliveryRecord] = useState(null);
  const [deliveryCustOpen, setDeliveryCustOpen] = useState(false);
  const [userMap, setUserMap]             = useState({});
  const [userList, setUserList]           = useState([]);
  const [shipdayDrivers, setShipdayDrivers]   = useState([]);
  const [shipdayDriverMap, setShipdayDriverMap] = useState({});
  const { socket }                        = useSocket();
  const { authState }                     = useAuth();

  // ── Chat panel ──
  const [showChatPanel, setShowChatPanel]         = useState(true);
  const [activeChats, setActiveChats]             = useState([]);
  const [chatsLoading, setChatsLoading]           = useState(false);
  const [chatsLoadingMore, setChatsLoadingMore]   = useState(false);
  const [hasMoreChats, setHasMoreChats]           = useState(true);
  const [chatSearch, setChatSearch]               = useState("");
  const [chatSearchFilter, setChatSearchFilter]   = useState(0);
  const [selectedChat, setSelectedChat]           = useState(null);
  const [flashingChatIds, setFlashingChatIds]     = useState(new Set());
  const chatScrollRef                             = useRef(null);
  const watiMsgProcessedRef                       = useRef(0);
  const selectedChatRef                           = useRef(null);
  const { qv4Chatlist, qv4ChatsLoading }          = useWatiChat();
  const { watiChatUpdates }                       = useWatiSocket();

  const [search, setSearch]           = useState("");
  const [payFilter, setPayFilter]     = useState(null);
  const [purFilter, setPurFilter]     = useState(null);

  useEffect(() => {
    axios.get(API_DOMAIN + "/auth/users/full", {
      headers: { accessToken: localStorage.getItem("accessToken") },
    }).then(res => {
      const raw = res.data?.data ?? res.data ?? [];
      const list = raw
        .filter(u => u.approved)
        .map(u => ({
          id: u.id,
          fullName: [u.name, u.surname].filter(Boolean).join(" ") || u.username || `#${u.id}`,
          user_type: u.user_type?.trim() || "",
        }));
      const map = {};
      list.forEach(u => { map[u.id] = u.fullName; });
      setUserMap(map);
      setUserList(list);
    }).catch(() => {});

    axios.get(API_DOMAIN + "/shipday/get_all_drivers_info", {
      headers: { accessToken: localStorage.getItem("accessToken") },
    }).then(res => {
      const list = (res.data?.data || []).map(d => ({ value: d.id, label: d.name }));
      const map = {};
      list.forEach(d => { map[d.value] = d.label; });
      setShipdayDrivers(list);
      setShipdayDriverMap(map);
    }).catch(() => {});
  }, []);

  const fetchActiveChats = async (search = chatSearch, filter = chatSearchFilter) => {
    setChatsLoading(true);
    setHasMoreChats(true);
    try {
      const res = await axios.post(
        CHAT_API,
        { channelIds: "", search_filter: filter, search: search.trim(), userId: authState?.user?.id },
        { headers: authHeaders() }
      );
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        const mapped = raw.map(mapChatItem);
        setActiveChats(mapped);
        if (raw.length < 100) setHasMoreChats(false);
        setChatsLoading(false);
        return mapped;
      }
    } catch {}
    setChatsLoading(false);
    return [];
  };

  const loadMoreChats = async () => {
    if (chatsLoadingMore || !hasMoreChats || chatsLoading) return;
    setChatsLoadingMore(true);
    try {
      const lastId = activeChats[activeChats.length - 1]?.conversationId;
      const res = await axios.post(
        CHAT_API,
        { channelIds: "", search_filter: chatSearchFilter, search: chatSearch.trim(), lastId, userId: authState?.user?.id },
        { headers: authHeaders() }
      );
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        const existingIds = new Set(activeChats.map(c => c.id));
        setActiveChats(prev => [...prev, ...raw.map(mapChatItem).filter(c => !existingIds.has(c.id))]);
        if (raw.length < 100) setHasMoreChats(false);
      }
    } catch {}
    setChatsLoadingMore(false);
  };

  const openChatByPhone = async (rawPhone) => {
    const normalized = (rawPhone || "").replace(/\D/g, "");
    if (!normalized) return;

    const matchPhone = (chat) => {
      const cp = (chat.phone || "").replace(/\D/g, "");
      return cp.endsWith(normalized) || normalized.endsWith(cp);
    };

    // Reveal panel if hidden; use a small delay so it mounts before we scroll
    const wasHidden = !showChatPanel;
    if (wasHidden) setShowChatPanel(true);

    let target = activeChats.find(matchPhone);

    if (!target) {
      // Not in loaded list — search by phone
      setChatSearch(normalized);
      const results = await fetchActiveChats(normalized, 0);
      target = (results || []).find(matchPhone);
    }

    if (!target) return;

    setSelectedChat(target);
    setFlashingChatIds(new Set([target.id]));
    setTimeout(() => setFlashingChatIds(new Set()), 1800);
    // Allow panel + search results to render before scrolling
    setTimeout(() => {
      document.getElementById(`chat-card-${target.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, wasHidden ? 300 : 100);
  };

  // Seed from global cache (same as QV4)
  useEffect(() => {
    if (qv4Chatlist.length > 0) {
      setActiveChats(qv4Chatlist.map(mapChatItem));
      setHasMoreChats(qv4Chatlist.length >= 100);
    } else if (!qv4ChatsLoading) {
      fetchActiveChats();
    }
  }, [qv4Chatlist, qv4ChatsLoading]); // eslint-disable-line

  useEffect(() => { selectedChatRef.current = selectedChat?.id ?? null; }, [selectedChat]);

  // Live chat updates — mirrors QuerysetV4 logic (incoming + outgoing paths)
  useEffect(() => {
    if (watiChatUpdates.length <= watiMsgProcessedRef.current) return;
    const newUpdates = watiChatUpdates.slice(watiMsgProcessedRef.current);
    watiMsgProcessedRef.current = watiChatUpdates.length;

    newUpdates.forEach(update => {
      if (update.target !== "sendTextMessage") return;
      const conv = update["0"]?.message?.conViewModel ?? null;
      const convId = conv?.id ?? update["0"]?.conversationId;
      const isIncoming = update["0"]?.message?.isOwner === false;
      const isOpen = selectedChatRef.current === convId;
      if (!convId) return;

      if (conv) {
        const updatedChat = mapChatItem({
          id: conv.id,
          name: conv.name,
          lastUpdated: conv.lastUpdated,
          lastIncomingDate: conv.lastIncomingDate,
          type: conv.type,
          text: conv.text || update["0"].message.text,
          messageType: update["0"].messageType,
          unreadCount: isOpen ? 0 : conv.unreadCount,
          isStar: conv.isStar,
          whatsappId: conv.whatsappId,
          activeTicketType: conv.activeTicketType,
          ticket: conv.ticket,
          remainingTime: conv.remainingTime,
        });
        setActiveChats(prev => {
          const exists = prev.some(c => c.id === updatedChat.id);
          const next = exists
            ? prev.map(c => c.id === updatedChat.id ? updatedChat : c)
            : [...prev, updatedChat];
          return [...next].sort((a, b) => {
            const aT = a.last_message_time?.isValid() ? a.last_message_time.valueOf() : 0;
            const bT = b.last_message_time?.isValid() ? b.last_message_time.valueOf() : 0;
            return bT - aT;
          });
        });
        if (isOpen) setSelectedChat(updatedChat);
      } else {
        const msgType = (update["0"].messageType || "text").toLowerCase();
        const rawText = update["0"]?.message?.text ?? null;
        let lastMsg;
        if (msgType === "audio") lastMsg = "Voice message";
        else if (msgType === "image") lastMsg = rawText || "Photo";
        else if (msgType === "video") lastMsg = rawText || "Video";
        else if (msgType === "document") lastMsg = rawText || "Document";
        else lastMsg = rawText;

        if (lastMsg != null) {
          try { sessionStorage.setItem(`wati_out_${convId}`, JSON.stringify({ text: lastMsg, time: Date.now() })); } catch {}
          setActiveChats(prev => {
            const next = prev.map(c =>
              (c.id === convId || c.conversationId === convId)
                ? { ...c, last_message: lastMsg, last_message_time: dayjs() }
                : c
            );
            return [...next].sort((a, b) => {
              const aT = a.last_message_time?.isValid() ? a.last_message_time.valueOf() : 0;
              const bT = b.last_message_time?.isValid() ? b.last_message_time.valueOf() : 0;
              return bT - aT;
            });
          });
        }
      }

      if (isIncoming) {
        setFlashingChatIds(prev => new Set([...prev, convId]));
        setTimeout(() => setFlashingChatIds(prev => { const n = new Set(prev); n.delete(convId); return n; }), 2000);
      }
    });
  }, [watiChatUpdates]);

  const fetchRecords = () => {
    setLoading(true);
    axios
      .get(`${API_DOMAIN}/query_after_sales/get`, {
        headers: authHeaders(),
      })
      .then(res => { if (res.data?.success) setRecords(res.data.data ?? []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRecords(); }, []); // eslint-disable-line

  // Real-time updates from backend (delivery creation, webhook status changes)
  useEffect(() => {
    if (!socket) return;
    const applySocketUpdate = (data) => {
      const afterSales      = data?.after_sales ?? data?.data?.after_sales;
      const deliveryTask    = data?.current_delivery_task ?? data?.delivery_task ?? data?.data?.current_delivery_task;
      if (!afterSales?.id) return;
      handleUpdate({ id: afterSales.id, ...afterSales, ...(deliveryTask ? { current_delivery_task: deliveryTask } : {}) });
    };
    socket.on("query:after_sales_updated", applySocketUpdate);
    socket.on("query:updated",             applySocketUpdate);
    socket.on("query:task_moved",          applySocketUpdate);
    return () => {
      socket.off("query:after_sales_updated", applySocketUpdate);
      socket.off("query:updated",             applySocketUpdate);
      socket.off("query:task_moved",          applySocketUpdate);
    };
  }, [socket]); // eslint-disable-line

  const filtered = useMemo(() => records.filter(r => {
    if (payFilter && r.payment_status !== payFilter) return false;
    if (purFilter && r.purchase_status !== purFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        String(r.query_id).toLowerCase().includes(q) ||
        (r.so_number              || "").toLowerCase().includes(q) ||
        (r.po_number              || "").toLowerCase().includes(q) ||
        (r.customer?.name         || "").toLowerCase().includes(q) ||
        (r.customer?.phone        || "").toLowerCase().includes(q) ||
        (r.vehicle?.brand         || "").toLowerCase().includes(q) ||
        (r.vehicle?.model         || "").toLowerCase().includes(q) ||
        (r.parts || []).some(p => (p.part_name || "").toLowerCase().includes(q))
      );
    }
    return true;
  }), [records, search, payFilter, purFilter]);

  const grouped = COLUMNS.reduce((acc, col) => {
    acc[col.key] = filtered.filter(r => getColumn(r) === col.key);
    return acc;
  }, {});

  const handleUpdate = (patch) => {
    setRecords(prev => prev.map(r => r.id === patch.id ? { ...r, ...patch } : r));
    setSelected(prev => prev?.id === patch.id ? { ...prev, ...patch } : prev);
  };

  const handlePatch = async (id, field, value) => {
    if (field === "__openDelivery") {
      const rec = records.find(r => r.id === id);
      if (rec) setDeliveryRecord(rec);
      return;
    }
    const orig = records.find(r => r.id === id);
    if (!orig) return;
    handleUpdate({ ...orig, [field]: value });
    try {
      const res = await axios.post(
        `${API_DOMAIN}/query_after_sales/update`,
        { id, [field]: value },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      const updated = res.data?.data?.after_sales ?? {};
      if (Object.keys(updated).length) handleUpdate({ ...orig, [field]: value, ...updated });
    } catch {
      handleUpdate(orig);
      message.error("Failed to update");
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 16px",
        borderBottom: "1px solid #f0f0f0",
        backgroundColor: "#fff",
        flexShrink: 0,
        overflow: "hidden",
      }}>
        {loading && <Spin size="small" />}
        <Input
          prefix={<SearchOutlined style={{ color: "#bbb" }} />}
          placeholder="Query #, customer, vehicle, part…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          style={{ flex: 1, minWidth: 140 }}
          size="middle"
        />
        <Select
          placeholder="Payment status"
          value={payFilter}
          onChange={setPayFilter}
          allowClear
          style={{ flex: 1, minWidth: 120 }}
          options={Object.entries(PAYMENT_CFG).map(([k, v]) => ({ value: k, label: v.label }))}
        />
        <Select
          placeholder="Purchase status"
          value={purFilter}
          onChange={setPurFilter}
          allowClear
          style={{ flex: 1, minWidth: 140 }}
          options={Object.entries(PURCHASE_CFG).map(([k, v]) => ({ value: k, label: v.label }))}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchRecords} loading={loading} size="middle">
          Refresh
        </Button>
        <Button
          icon={<MessageOutlined />}
          onClick={() => setShowChatPanel(v => !v)}
          type={showChatPanel ? "primary" : "default"}
          size="middle"
        >
          {showChatPanel ? "Hide Chats" : "Show Chats"}
        </Button>
      </div>

      {/* Board */}
      <div style={{
        flex: 1, minHeight: 0, overflow: "hidden",
        display: "flex", flexDirection: "row",
        gap: 16, padding: 16,
        background: "#dde1ea",
      }}>
        {/* Chat sidebar */}
        {showChatPanel && (
          <ChatSidebarColumn
            col={{ key: "chats", label: "Chats", icon: <MessageOutlined />, color: "#25d366" }}
            activeChats={activeChats}
            setActiveChats={setActiveChats}
            chatsLoading={chatsLoading}
            chatsLoadingMore={chatsLoadingMore}
            hasMoreChats={hasMoreChats}
            chatSearch={chatSearch}
            setChatSearch={setChatSearch}
            chatSearchFilter={chatSearchFilter}
            setChatSearchFilter={setChatSearchFilter}
            fetchActiveChats={fetchActiveChats}
            loadMoreChats={loadMoreChats}
            selectedChat={selectedChat}
            setSelectedChat={setSelectedChat}
            queryMatchesByPhone={{}}
            scrollToSingleQuery={() => {}}
            flashingChatIds={flashingChatIds}
            authState={authState}
            onBulkTemplateOpen={() => {}}
            chatScrollRef={chatScrollRef}
          />
        )}

        {COLUMNS.map(col => {
          const cards = grouped[col.key] || [];
          return (
            <div
              key={col.key}
              style={{
                flex: 1, minWidth: 0,
                display: "flex", flexDirection: "column",
                backgroundColor: "#fff",
                borderRadius: 8,
                border: "1px solid #e8e8e8",
                borderTop: `4px solid ${col.color}`,
                overflow: "hidden",
              }}
            >
              <div style={{
                padding: "10px 14px",
                backgroundColor: "#fff",
                borderBottom: "1px solid #f0f0f0",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexShrink: 0,
              }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: "#262626", display: "flex", alignItems: "center", gap: 6 }}>
                  {col.icon} {col.label}
                </span>
                <Badge count={cards.length} showZero style={{ backgroundColor: "#8c8c8c" }} />
              </div>

              <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 12 }}>
                {loading && cards.length === 0 ? (
                  <div style={{ display: "flex", justifyContent: "center", paddingTop: 40 }}>
                    <Spin />
                  </div>
                ) : col.key === "completed" ? (() => {
                  const warrantyCards = cards.filter(r => isUnderWarranty(r));
                  const closedCards   = cards.filter(r => !isUnderWarranty(r));
                  const renderCards = (list) => list.length === 0
                    ? <Empty description="None" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "12px 0" }} />
                    : list.map(r => <AfterSalesCard key={r.id} record={r} userMap={userMap} userList={userList} shipdayDrivers={shipdayDrivers} shipdayDriverMap={shipdayDriverMap} onQuickPatch={handlePatch} onClick={() => setSelected(r)} onOpenChat={openChatByPhone} />);
                  return (
                    <Collapse
                      defaultActiveKey={["warranty", "closed"]}
                      bordered={false}
                      style={{ background: "transparent" }}
                      items={[
                        {
                          key: "warranty",
                          label: (
                            <span style={{ fontWeight: 600, fontSize: 12 }}>
                              Under Warranty <Badge count={warrantyCards.length} showZero style={{ backgroundColor: "#52c41a", marginLeft: 4 }} />
                            </span>
                          ),
                          children: <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{renderCards(warrantyCards)}</div>,
                          style: { marginBottom: 4, background: "#f6ffed", borderRadius: 6 },
                        },
                        {
                          key: "closed",
                          label: (
                            <span style={{ fontWeight: 600, fontSize: 12 }}>
                              Closed <Badge count={closedCards.length} showZero style={{ backgroundColor: "#8c8c8c", marginLeft: 4 }} />
                            </span>
                          ),
                          children: <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{renderCards(closedCards)}</div>,
                          style: { background: "#fafafa", borderRadius: 6 },
                        },
                      ]}
                    />
                  );
                })() : col.key === "delivery" ? (() => {
                  const readyCards      = cards.filter(r => !r.current_delivery_task);
                  const dispatchedCards = cards.filter(r =>  r.current_delivery_task);
                  const renderCards = (list) => list.length === 0
                    ? <Empty description="None" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "12px 0" }} />
                    : list.map(r => <AfterSalesCard key={r.id} record={r} userMap={userMap} userList={userList} shipdayDrivers={shipdayDrivers} shipdayDriverMap={shipdayDriverMap} onQuickPatch={handlePatch} onClick={() => setSelected(r)} onOpenChat={openChatByPhone} />);
                  return (
                    <Collapse
                      defaultActiveKey={["ready", "dispatched"]}
                      bordered={false}
                      style={{ background: "transparent" }}
                      items={[
                        {
                          key: "ready",
                          label: (
                            <span style={{ fontWeight: 600, fontSize: 12 }}>
                              Ready <Badge count={readyCards.length} showZero style={{ backgroundColor: "#1677ff", marginLeft: 4 }} />
                            </span>
                          ),
                          children: <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{renderCards(readyCards)}</div>,
                          style: { marginBottom: 4, background: "#f0f5ff", borderRadius: 6 },
                        },
                        {
                          key: "dispatched",
                          label: (
                            <span style={{ fontWeight: 600, fontSize: 12 }}>
                              Dispatched <Badge count={dispatchedCards.length} showZero style={{ backgroundColor: "#fa8c16", marginLeft: 4 }} />
                            </span>
                          ),
                          children: <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{renderCards(dispatchedCards)}</div>,
                          style: { background: "#fff7e6", borderRadius: 6 },
                        },
                      ]}
                    />
                  );
                })() : cards.length === 0 ? (
                  <Empty description="No records" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />
                ) : (
                  cards.map(r => (
                    <AfterSalesCard key={r.id} record={r} userMap={userMap} userList={userList} shipdayDrivers={shipdayDrivers} shipdayDriverMap={shipdayDriverMap} onQuickPatch={handlePatch} onClick={() => setSelected(r)} onOpenChat={openChatByPhone} />
                  ))
                )}
              </div>
            </div>
          );
        })}

      </div>

      <ActiveChatDrawer
        chat={selectedChat}
        pinned={false}
        onPinToggle={() => {}}
        onClose={() => setSelectedChat(null)}
        onWidthChange={() => {}}
        onChatPatch={(id, patch) =>
          setActiveChats(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c))
        }
        onQueryCreated={() => {}}
      />

      <AfterSalesDrawer
        record={selectedRecord}
        onClose={() => setSelected(null)}
        userMap={userMap}
        userList={userList}
        onUpdate={handleUpdate}
        onCreateDelivery={rec => setDeliveryRecord(rec)}
      />

      <CreateDeliveryModal
        open={!!deliveryRecord}
        record={deliveryRecord}
        onClose={() => setDeliveryRecord(null)}
        onCreated={({ after_sales } = {}) => {
          if (after_sales) handleUpdate({ ...deliveryRecord, ...after_sales });
          setDeliveryRecord(null);
        }}
        onEditCustomer={() => setDeliveryCustOpen(true)}
      />

      <CustomerEditDrawer
        open={deliveryCustOpen}
        customer={deliveryRecord?.customer ? {
          customer_id:            deliveryRecord.customer.id,
          customer_name:          deliveryRecord.customer.name,
          customer_mobile:        deliveryRecord.customer.phone,
          customer_email:         deliveryRecord.customer.email,
          customer_addresses_new: deliveryRecord.customer.addresses,
        } : null}
        defaultTab="addresses"
        onClose={() => setDeliveryCustOpen(false)}
        onSaved={(saved) => {
          if (deliveryRecord) {
            const freshAddresses = saved.customer_addresses_new ?? saved.addresses ?? deliveryRecord.customer.addresses;
            const updatedRec = {
              ...deliveryRecord,
              customer: { ...deliveryRecord.customer, ...saved, addresses: freshAddresses },
            };
            setDeliveryRecord(updatedRec);
            handleUpdate(updatedRec);
          }
          setDeliveryCustOpen(false);
        }}
      />
    </div>
  );
}
